import { useBoolean } from 'usehooks-ts';
import { Box, CheckIcon, ChevronDownIcon, CircleIcon, HStack, Popover, Pressable } from 'native-base';
import { find, isEmpty, map } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Text } from '@pimm/base';
import { LearningPathDto } from '@pimm/services/lib/sms-workforce/types';

type DropdownLearningPathsProps = {
  options?: LearningPathDto[];
  value?: string;
  onChange: (_learningPathId: string) => void;
};

export const DropdownLearningPaths = ({ options, value, ..._props }: DropdownLearningPathsProps) => {
  const isOpen = useBoolean();
  const { t } = useTranslation();

  const opened = isOpen.value;
  const learningPath = find(options, ['id', value]);

  const handleChange = (_learningPath: LearningPathDto) => () => {
    isOpen.setFalse();
    if (_learningPath.id === value) return;
    _props.onChange(_learningPath.id!);
  };

  return (
    <Popover
      isOpen={opened}
      onOpen={isOpen.setTrue}
      onClose={isOpen.setFalse}
      _backdrop={{ bg: opened ? 'black' : undefined }}
      placement="bottom left"
      useRNModal
      trigger={_triggerProps => (
        <Pressable isDisabled={!options || options.length === 1} {..._triggerProps}>
          {!isEmpty(learningPath) && (
            <HStack space={2} alignItems="center" h={9}>
              <Text size="lg" fontWeight={600} color="black" lineHeight="lg">
                {t('app_insights:learning_path')}
              </Text>

              {!!learningPath?.name && (
                <>
                  <CircleIcon size={1} color="black" />
                  <Text size="lg" fontWeight={500} color="gray.700" lineHeight="lg">
                    {learningPath?.name}
                  </Text>
                </>
              )}
              <ChevronDownIcon size="xs" color="gray.600" />
            </HStack>
          )}
        </Pressable>
      )}
    >
      <Popover.Content accessibilityLabel="Select a market" p={0} borderColor="white" w={350} opacity={opened ? 1 : 0}>
        <Popover.Body p={0}>
          {map(options, (_option, index) => {
            const isActive = _option.id === value;
            return (
              <Pressable key={`learning-paths.${_option.id}.${index}`} _pressed={{ bg: 'gray.200' }} onPress={handleChange(_option)}>
                <HStack
                  space={2}
                  alignItems="center"
                  pl={2.5}
                  pr={1}
                  minH={10}
                  borderTopWidth={index ? 1 : 0}
                  bg={isActive ? 'gray.50' : undefined}
                >
                  <Box flex={1}>
                    <Text size="md" fontWeight={isActive ? 600 : 500} color={isActive ? 'black' : 'gray.700'}>
                      {_option.name}
                    </Text>
                  </Box>
                  <Box alignItems="center" justifyContent="center" boxSize={6}>
                    {isActive && <CheckIcon color="black" />}
                  </Box>
                </HStack>
              </Pressable>
            );
          })}
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
};
